import React, { useEffect, useState } from "react";

import { Button, Col, Form, Row, Card } from "react-bootstrap";

import {
  CardComponent,
  ConfirmationModal,
  AvatarPicture,
  InputEdit,
} from "components";

import { validatePassword } from "../../utils/helpers/validators";

import { useAuth, useUsersApi, useToast } from "utils/hooks";

import { useNavigate } from "react-router-dom";

export const Profile = () => {
  const api = useUsersApi();
  const navigate = useNavigate();
  const { selected_tenant_id, userId, onTenantLogout, onRefreshToken } =
    useAuth();
  const { openToast } = useToast();

  const [isUserDetailsValidated, setUserDetailsValidated] = useState(false);

  const [isUserPasswordValidated, setUserPasswordValidated] = useState(false);

  const [userProfile, setUserProfile] = useState([]);

  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  const [userDetails, setUserDetails] = useState({
    first_name: "",
    last_name: "",
    current_password: "",
    new_password: "",
  });

  const [userDetailsError, setUserDetailsError] = useState({
    first_name: null,
    last_name: null,
  });

  const [passwordError, setPasswordError] = useState({
    current_password: null,
    new_password: null,
  });

  const [modalProps, setModalProps] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "",
  });

  const getUserProfile = async (userId) => {
    try {
      let response = await api.getUserProfile(userId);
      response = response.data;
      // Sort tenants and their groups alphabetically
      response.tenants.sort((a, b) => a.tenantName.localeCompare(b.tenantName));

      setUserProfile(response);
      setUserDetails({
        first_name: response.first_name,
        last_name: response.last_name,
        username: response.username,
      });
      setNotificationsEnabled(response.notificationEnabled)
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateUserProfile = async (payload) => {
    try {
      const response = await api.updateUserProfile({
        first_name: payload.first_name,
        last_name: payload.last_name,
      });

      if (response.status !== "SUCCESS") {
        openToast({
          message: `${response.message}`,
          type: "danger",
          autohide: true,
          delay: 5000,
        });
        return;
      }

      openToast({
        message: `Personal info changed successfully`,
        type: "success",
        autohide: true,
        delay: 5000,
      });

      getUserProfile(userId);

      return response;
    } catch (error) {
      openToast({
        message: `Request failed`,
        type: "danger",
        autohide: true,
        delay: 5000,
      });
      throw error;
    }
  };

  const enableNotifications = async (isEnabled) => {
    try {
      const response = await api.enableNotifications(isEnabled);

      if (response.status !== "SUCCESS") {
        openToast({
          message: `${response.message}`,
          type: "danger",
          autohide: true,
          delay: 5000,
        });
        return;
      }
      
      getUserProfile(userId);

      return response;
    } catch (error) {
      openToast({
        message: `Request failed`,
        type: "danger",
        autohide: true,
        delay: 5000,
      });
      throw error;
    }
  };

  const updateUserPassword = async (payload) => {
    try {
      const response = await api.updateUserPassword({
        current_password: payload.current_password,
        new_password: payload.new_password,
      });

      if (response.status !== "SUCCESS") {
        openToast({
          message: `${response.message}`,
          type: "danger",
          autohide: true,
          delay: 5000,
        });
        return;
      }

      openToast({
        message: `Password changed successfully`,
        type: "success",
        autohide: true,
        delay: 5000,
      });

      getUserProfile(userId);
      return response;
    } catch (error) {
      openToast({
        message: `Request failed`,
        type: "danger",
        autohide: true,
        delay: 5000,
      });
      throw error;
    }
  };

  const updateDefaultTenant = async (tenantId) => {
    try {
      const selectedTenant = userProfile.tenants.find(
        (tenant) => tenant.tenantId === tenantId,
      );

      if (!selectedTenant) throw new Error("Tenant not found");

      const response = await api.updateDefaultTenant({
        tenantId: selected_tenant_id,
        userId: userProfile.userId,
        tenantName: selectedTenant.tenantId,
        userEmailAddress: userProfile.email_address,
        isDefault: true,
      });

      await getUserProfile(userId);
      return response;
    } catch (error) {
      alert(error);
    }
  };

  const leaveTenant = async (tenantName, tenantId) => {
    try {
      if (tenantId === selected_tenant_id) {
        openModal({
          title: "Are you sure you want to leave current tenant?",
          message:
            "If you leave this tenant you will be redirected in order to select a new tenant.",
          type: "warning",
          onConfirm: async () => {
            const response = await api.leaveTenantById(tenantId);
            onTenantLogout();
          },
        });
      } else {
        const response = await api.leaveTenantById(tenantId);
        await getUserProfile(userId);
        openToast({
          message: `You have left the tenant ${tenantName}`,
          type: "success",
          autohide: true,
          delay: 5000,
        });
      }
    } catch (error) {
      openToast({
        message: `Request failed`,
        type: "danger",
        autohide: true,
        delay: 5000,
      });
    }
  };

  const openModal = ({ title, message, onConfirm, type = "primary" }) => {
    setModalProps({
      show: true,
      title,
      message,
      onConfirm,
      type,
    });
  };

  const closeModal = () => {
    setModalProps((prev) => ({ ...prev, show: false }));
  };

  const onToggleNotifications = (e) => {
    const isChecked = e.target.checked;
    setNotificationsEnabled(isChecked);
    enableNotifications(isChecked)
  };

  useEffect(() => {
    if (userDetails.first_name?.trim() && userDetails.last_name?.trim())
      setUserDetailsValidated(true);
    else setUserDetailsValidated(false);

    if (
      userDetails.current_password?.trim() &&
      userDetails.new_password?.trim() &&
      userDetails.confirm_password?.trim() &&
      userDetails.new_password === userDetails.confirm_password
    )
      setUserPasswordValidated(true);
    else setUserPasswordValidated(false);
  }, [userDetails]);

  useEffect(() => {
    getUserProfile(userId);
  }, []);

  return (
    <>
      <Row className="m-3">
        <CardComponent title={"Personal info"}>
          <Row className="m-1">
            <Col
              className="shadow col-4 login-form text-center"
              style={{ opacity: 0.9 }}
            >
              <AvatarPicture
                width={200}
                height={200}
                {...(userProfile.first_name || userProfile.last_name
                  ? {
                      first_name: userProfile.first_name,
                      last_name: userProfile.last_name,
                    }
                  : { email_address: userProfile.email_address })}
              />
              <Row>
                <Form onSubmit={(e) => e.preventDefault()}>
                  <Form.Group as={Col} controlId="formProjectDate">
                    <Form.Label>
                      <b>
                        {userProfile.first_name && userProfile.last_name
                          ? userProfile.first_name + " " + userProfile.last_name
                          : userProfile.username}
                      </b>
                    </Form.Label>
                  </Form.Group>

                  <Form.Group as={Col} controlId="formProjectDate">
                    <Form.Label>Email: {userProfile.email_address}</Form.Label>
                  </Form.Group>
                  <Form.Group as={Col} controlId="formProjectDate">
                    <Form.Label>
                      Active tenant:{" "}
                      {
                        userProfile?.tenants?.find(
                          (tenant) => tenant.tenantId === selected_tenant_id,
                        )?.tenantName
                      }
                      <Button
                        className="w-100"
                        variant="link"
                        size="sm"
                        onClick={(e) => {
                          onTenantLogout();
                          navigate("/select-tenant");
                        }}
                      >
                        Switch tenant
                      </Button>
                    </Form.Label>
                  </Form.Group>
                </Form>
              </Row>
            </Col>
            <Col className="shadow login-form ms-2" style={{ opacity: 0.9 }}>
              <Row>
                <Form className="col-6" onSubmit={(e) => e.preventDefault()}>
                  <Form.Group
                    as={Col}
                    className="mb-1"
                    controlId="formProjectDate"
                  >
                    <Form.Label>Change personal info</Form.Label>
                    <InputEdit
                      placeholder={"First Name"}
                      value={userProfile?.first_name || ""}
                      error={userDetailsError?.first_name}
                      validationPattern={/[^a-zA-Z0-9 -]/g}
                      onFocus={(e) => {
                        setUserDetailsError({
                          ...userDetailsError,
                          first_name: null,
                        });
                      }}
                      onBlur={(e) => {
                        if (e.target.value === null || e.target.value === "")
                          setUserDetailsError({
                            ...userDetailsError,
                            first_name: "Cannot be empty",
                          });
                      }}
                      onChange={(e) => {
                        setUserDetails({
                          ...userDetails,
                          first_name: e.target.value,
                        });
                      }}
                      onKeyDown={(e) => {
                        // Prevent numbers from being entered
                        if (!/^[a-zA-Z\s]*$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    className="mb-1"
                    controlId="formProjectDate"
                  >
                    <InputEdit
                      placeholder={"Last Name"}
                      value={userProfile?.last_name || ""}
                      error={userDetailsError?.last_name}
                      validationPattern={/[^a-zA-Z0-9 -]/g}
                      onFocus={(e) => {
                        setUserDetailsError({
                          ...userDetailsError,
                          last_name: null,
                        });
                      }}
                      onBlur={(e) => {
                        if (e.target.value === null || e.target.value === "")
                          setUserDetailsError({
                            ...userDetailsError,
                            last_name: "Cannot be empty",
                          });
                      }}
                      onChange={(e) => {
                        setUserDetails({
                          ...userDetails,
                          last_name: e.target.value,
                        });
                      }}
                      onKeyDown={(e) => {
                        // Prevent numbers from being entered
                        if (!/^[a-zA-Z\s]*$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    className="mb-1"
                    controlId="formProjectDate"
                  >
                    <InputEdit
                      // label={"Email"}
                      value={userProfile?.email_address || ""}
                      disabled={true}
                      cleanWhitespaces={true}
                    />
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    className="mb-1 mt-4 d-flex justify-content-end"
                    controlId="formProjectDate"
                  >
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={!isUserDetailsValidated}
                      onClick={(e) => {
                        updateUserProfile(userDetails);
                      }}
                    >
                      Save details
                    </Button>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    className="mb-1 mt-4"
                    controlId="formProjectDate"
                  >
                    <Form.Label>Change password</Form.Label>
                    <InputEdit
                      placeholder={"Current password"}
                      type={"password"}
                      error={passwordError?.current_password}
                      value={userDetails.current_password}
                      onFocus={(e) =>
                        setPasswordError({
                          ...passwordError,
                          current_password: null,
                        })
                      }
                      onBlur={(e) => {
                        if (e.target.value === null || e.target.value === "") {
                          setPasswordError({
                            ...passwordError,
                            current_password: "Cannot be empty",
                          });
                          setUserPasswordValidated(false);
                        }
                      }}
                      onChange={(e) => {
                        setUserDetails({
                          ...userDetails,
                          current_password: e.target.value,
                        });
                      }}
                    />
                    <InputEdit
                      placeholder={"New password"}
                      value={userDetails.new_password}
                      type={"password"}
                      error={passwordError?.new_password}
                      onFocus={(e) =>
                        setPasswordError({
                          ...passwordError,
                          new_password: null,
                        })
                      }
                      onBlur={(e) => {
                        if (!validatePassword(e.target.value)) {
                          setPasswordError({
                            ...passwordError,
                            new_password:
                              "The new password must be at least 8 characters long",
                          });
                          setUserPasswordValidated(false);
                        } else if (
                          e.target.value !== userDetails.confirm_password
                        ) {
                          setPasswordError({
                            ...passwordError,
                            new_password: "Password mismatch",
                          });
                          setUserPasswordValidated(false);
                        } else {
                          setPasswordError({
                            ...passwordError,
                            new_password: null,
                          });
                          // setUserPasswordValidated(true)
                        }
                      }}
                      onChange={(e) => {
                        if (!validatePassword(e.target.value)) {
                          setPasswordError({
                            ...passwordError,
                            new_password:
                              "The new password must be at least 8 characters long",
                          });
                          setUserPasswordValidated(false);
                        } else {
                          setPasswordError(null);
                          // setUserPasswordValidated(true)
                        }
                        setUserDetails({
                          ...userDetails,
                          new_password: e.target.value,
                        });
                      }}
                    />
                    <InputEdit
                      placeholder={"Confirm new password"}
                      value={userDetails.confirm_password}
                      type={"password"}
                      error={passwordError?.new_password}
                      onFocus={(e) =>
                        setPasswordError({
                          ...passwordError,
                          new_password: null,
                        })
                      }
                      onBlur={(e) => {
                        if (e.target.value !== userDetails.new_password) {
                          setPasswordError({
                            ...passwordError,
                            new_password: "Password mismatch",
                          });
                          setUserPasswordValidated(false);
                          return;
                        } else if (
                          !validatePassword(userDetails.new_password)
                        ) {
                          setPasswordError({
                            ...passwordError,
                            new_password:
                              "The new password must be at least 8 characters long",
                          });
                          setUserPasswordValidated(false);
                        } else {
                          setPasswordError(null);
                        }
                      }}
                      onChange={(e) => {
                        setUserDetails({
                          ...userDetails,
                          confirm_password: e.target.value,
                        });
                      }}
                    />
                  </Form.Group>

                  <Form.Group
                    as={Col}
                    className="mb-1 mt-4 d-flex justify-content-end"
                    controlId="formProjectDate"
                  >
                    <Button
                      variant="outline-warning"
                      size="sm"
                      disabled={!isUserPasswordValidated}
                      onClick={(e) => {
                        updateUserPassword(userDetails);
                      }}
                    >
                      Change password
                    </Button>
                  </Form.Group>
                </Form>
                <Form className="col-6">
                  <Form.Label>Notifcations</Form.Label>
                  <Col className="d-flex align-items-center justify-content-between">
                    <Form.Label>Email notifications</Form.Label>
                    <Form.Check
                      type="switch"
                      aria-label="radio 2"
                      onChange={onToggleNotifications}
                      checked={notificationsEnabled}
                    />
                  </Col>
                </Form>
              </Row>
            </Col>
          </Row>
        </CardComponent>
        <CardComponent title={"Available Tenants"}>
          <Form.Group as={Row} className="m-1">
            {userProfile?.tenants?.map((tenant, index) => (
              <Form
                key={index}
                className="shadow  login-form mt-2 pt-0"
                style={{ opacity: 0.9 }}
              >
                <Card
                  key={tenant.tenantId}
                  style={{ background: "transparent" }}
                >
                  <Row>
                    <Col>
                      <Row className="p-0 m-0 g-0">
                        <Col
                          xs={3}
                          className="text-start fw-bold"
                          style={{ color: "#177ddc" }}
                        >
                          Tenant Name:
                        </Col>
                        <Col xs={8} style={{ color: "#dbdbdb" }}>
                          {tenant.tenantName}
                        </Col>

                        <Col
                          xs={3}
                          className="text-start fw-bold"
                          style={{ color: "#177ddc" }}
                        >
                          Tenant ID:
                        </Col>
                        <Col xs={8} style={{ color: "#dbdbdb" }}>
                          {tenant.tenantId}
                        </Col>

                        <Col
                          xs={3}
                          className="text-start fw-bold"
                          style={{ color: "#177ddc" }}
                        >
                          Tenant Role:
                        </Col>
                        <Col xs={8} style={{ color: "#dbdbdb" }}>
                          {tenant.tenantRole}
                        </Col>

                        <Col
                          xs={3}
                          className="text-start fw-bold"
                          style={{ color: "#177ddc" }}
                        >
                          Networks and Roles:
                        </Col>
                        <Col xs={8} style={{ color: "#dbdbdb" }}>
                          {tenant.groups.length > 0
                            ? tenant.groups.map((group, i) => (
                                <div key={i}>
                                  {group.groupName} ({group.roleName})
                                </div>
                              ))
                            : (tenant.tenantRole === "Tenant User" && (
                                <div>No groups assigned</div>
                              )) ||
                              (tenant.tenantRole === "Tenant Owner" && (
                                <div>Administrator in all groups</div>
                              ))}
                        </Col>
                      </Row>
                    </Col>
                    <Col className="d-flex justify-content-end">
                      <div
                        className="d-flex flex-column justify-content-between"
                        style={{ width: "150px" }} // Adjust as needed
                      >
                        {tenant.isDefault !== true ? (
                          <Button
                            className="w-100"
                            variant="link"
                            size="sm"
                            value={tenant.tenantId}
                            onClick={(e) => {
                              const tenantId = e.target.value;

                              updateDefaultTenant(e.target.value);
                            }}
                          >
                            Select as default tenant
                          </Button>
                        ) : (
                          <span
                            className="text-center small text-success"
                            size="sm"
                          >
                            Default tenant
                          </span>
                        )}
                        {tenant.tenantRole !== "Tenant Owner" && (
                          <Button
                            className="w-100"
                            variant="outline-danger"
                            size="sm"
                            value={tenant.tenantId}
                            onClick={(e) => {
                              const tenantId = e.target.value;

                              leaveTenant(tenant.tenantName, tenantId);
                            }}
                          >
                            Leave tenant
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Form>
            ))}
          </Form.Group>
        </CardComponent>
        <ConfirmationModal {...modalProps} onClose={closeModal} />
      </Row>
    </>
  );
};
